import { ChangeEvent } from "react";
import { useTranslation } from "react-i18next";

import { Divider, Grid, Stack } from "@mui/material";
import { FormikProps } from "formik";

import { AddressFields } from "../../../../../../address-fields/AddressFields";
import { FormikTextField } from "../../../../../../formik/FormikTextField";
import { FormikYesNoAutocomplete } from "../../../../../../formik/FormikYesNoAutocomplete";
import { FormikIbanField } from "../../../../../../formik/formik-iban-field/FormikIbanField";
import { nameof } from "../../../../../../utils/nameof";
import {
  AssetInventoryMaxLength,
  AssetPersonalDataFormValues,
  createEmptyAccount,
} from "../../AssetPersonalData.types";
import { FieldWithInfo } from "../field-with-info/FieldWithInfo";
import { OverdrawnWarningBox } from "../overdrawn-warning-box/OverdrawnWarningBox";

interface Props {
  formik: FormikProps<AssetPersonalDataFormValues>;
  index: number;
}

export function AccountFields({ formik, index }: Props) {
  const { t } = useTranslation();
  const base = `accounts.${index}` as const;

  const account = formik.values.accounts[index];
  const hasFurtherAccount = account?.hasFurtherAccount ?? null;

  const clearSelectedBank = () => {
    if (formik.values.bankId) {
      void formik.setFieldValue(nameof<AssetPersonalDataFormValues>("bankId"), null);
    }
  };

  const onBankFieldEdit = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    formik.handleChange(event);
    clearSelectedBank();
  };

  const onBankAddressEdited = () => clearSelectedBank();

  const onFurtherAccountChange = (value: boolean | null) => {
    const current = formik.values.accounts;
    let next = current.map((a, i) => (i === index ? { ...a, hasFurtherAccount: value } : a));
    if (value === true && index === current.length - 1) {
      next = [...next, createEmptyAccount()];
    } else if (value !== true && current.length > index + 1) {
      next = next.slice(0, index + 1);
    }
    void formik.setFieldValue(nameof<AssetPersonalDataFormValues>("accounts"), next);
  };

  return (
    <Stack gap={2}>
      <Grid container rowSpacing={2} columnSpacing={2}>
        <Grid size={6}>
          <FormikTextField
            formik={formik}
            path={`${base}.bankName`}
            label={t("clients.client.debtSettlement.assetInventory.section1.bankName")}
            onChange={onBankFieldEdit}
            slotProps={{ input: { inputProps: { maxLength: AssetInventoryMaxLength.bankName } } }}
          />
        </Grid>
        <AddressFields
          formik={formik}
          streetField={`${base}.bankStreet`}
          houseNumberField={`${base}.bankHouseNumber`}
          zipCodeField={`${base}.bankZipCode`}
          cityField={`${base}.bankCity`}
          countryField={`${base}.bankCountry`}
          onValueEdited={onBankAddressEdited}
        />
        <Grid size={6}>
          <FormikIbanField formik={formik} path={`${base}.iban`} />
        </Grid>
        <Grid size={6}>
          <FieldWithInfo
            fieldLabel={t("clients.client.debtSettlement.assetInventory.section1.incomings")}
            info={t("clients.client.debtSettlement.assetInventory.section1.incomingsInfo")}
          >
            <FormikTextField
              formik={formik}
              path={`${base}.incomings`}
              label={t("clients.client.debtSettlement.assetInventory.section1.incomings")}
              slotProps={{ input: { inputProps: { maxLength: AssetInventoryMaxLength.incomings } } }}
            />
          </FieldWithInfo>
        </Grid>
        <Grid size={6}>
          <FormikYesNoAutocomplete
            formik={formik}
            path={`${base}.isOverdrawn`}
            label={t("clients.client.debtSettlement.assetInventory.section1.overdrawnQuestion")}
          />
        </Grid>
      </Grid>

      {account?.isOverdrawn === true && <OverdrawnWarningBox />}

      <Grid container rowSpacing={2} columnSpacing={2}>
        <Grid size={6}>
          <FieldWithInfo
            fieldLabel={t("clients.client.debtSettlement.assetInventory.section1.hasFurtherAccountQuestion")}
            info={t("clients.client.debtSettlement.assetInventory.section1.accountQuestionInfo")}
          >
            <FormikYesNoAutocomplete
              formik={formik}
              path={`${base}.hasFurtherAccount`}
              onValueChange={onFurtherAccountChange}
              label={t("clients.client.debtSettlement.assetInventory.section1.hasFurtherAccountQuestion")}
            />
          </FieldWithInfo>
        </Grid>
      </Grid>

      {hasFurtherAccount === true && (
        <>
          <Divider />
          <AccountFields formik={formik} index={index + 1} />
        </>
      )}
    </Stack>
  );
}
